'use client';

import { useEffect, useRef, useState } from 'react';

const batches = ['Beginners (6–14 yrs)', 'Intermediate (12–25 yrs)', 'Advanced (15+ yrs)', 'Ladies Special (18–50 yrs)'];

export default function RegisterSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    age: '',
    batch: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden, .reveal-hidden-left, .reveal-hidden-right').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit — backend integration point
    setSubmitted(true);
  };

  return (
    <section id="register" className="py-28 relative overflow-hidden" ref={sectionRef}>
      {/* Background */}
      <div className="absolute inset-0 bg-dark" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-5 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left — copy */}
          <div className="reveal-hidden-left text-white space-y-8">
            <div>
              <p className="text-accent font-bold uppercase tracking-[0.3em] text-[11px] mb-3">Join the Family</p>
              <h2 className="font-display text-5xl md:text-6xl font-black leading-tight">
                Start Your
                <br />
                <span className="font-display italic text-gradient-dark">Garba Journey</span>
                <br />
                Today.
              </h2>
            </div>
            <p className="text-white/65 text-lg leading-relaxed">
              Limited seats available for March 2026 batches. Register now to secure your spot and receive a free 
              trial class with Ravi sir.
            </p>

            <div className="space-y-4">
              {[
                { icon: '🎁', text: 'Free trial class for all new registrations' },
                { icon: '📞', text: 'Callback within 2 hours of registration' },
                { icon: '🏠', text: 'Training center: Paldi, Ahmedabad — 380007' },
                { icon: '⏰', text: 'Registration open until February 28, 2026' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <span className="text-xl shrink-0">{item.icon}</span>
                  <p className="text-white/75 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-white/10">
              <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Contact Directly</p>
              <p className="text-accent font-bold text-lg">+91 98765 43210</p>
              <p className="text-white/50 text-sm">info@ravisgoldengarba.com</p>
            </div>
          </div>

          {/* Right — Form */}
          <div className="reveal-hidden-right">
            <div className="glass-dark rounded-4xl p-8 md:p-10 shadow-warm-xl">
              {submitted ? (
                <div className="text-center py-12 space-y-5">
                  <div className="text-6xl">🎊</div>
                  <h3 className="font-display text-3xl font-bold text-white">Registered!</h3>
                  <p className="text-white/65 leading-relaxed">
                    Thank you! We&apos;ll call you within 2 hours to confirm your batch and schedule your free trial class.
                  </p>
                  <p className="text-accent font-semibold text-sm">आपका स्वागत है! Welcome to the family.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl font-bold text-white mb-2">Register for Garba Training</h3>
                  <p className="text-white/50 text-sm mb-7">Fill in your details and we&apos;ll get back to you shortly.</p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Priya Sharma"
                          className="form-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Age *</label>
                        <input
                          type="number"
                          name="age"
                          value={form.age}
                          onChange={handleChange}
                          required
                          placeholder="24"
                          min="5"
                          max="70"
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 98765 43210"
                        className="form-input"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="priya@gmail.com"
                        className="form-input"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Select Batch *</label>
                      <select
                        name="batch"
                        value={form.batch}
                        onChange={handleChange}
                        required
                        className="form-input"
                      >
                        <option value="">Choose your batch...</option>
                        {batches.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Message (Optional)</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Any questions or special requirements..."
                        className="form-input resize-none"
                      />
                    </div>

                    <button type="submit" className="btn-primary w-full text-base glow-pulse mt-2">
                      🪘 Register Now — Free Trial Included
                    </button>

                    <p className="text-white/35 text-xs text-center">
                      By registering, you agree to our terms. No payment required at this stage.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}